import { Typography } from '@mui/material';
import { type ExposureOrGroup, useSubmission } from 'api/submission';
import { ExposuresList, ExposuresListItem, ExposuresWrapper } from 'components/ExposuresWrapper';
import { Modal } from 'components/Modal';
import { displayToastMessage } from 'components/ToastMessage';
import { determineOwnerOfficerPositions } from 'helpers/utils';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { capitalize } from 'lodash-es';
import { type FC, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { IOwner } from './Owners.types';
import { OwnerModal } from './components/Modal';
import type { OwnerModalRef } from './components/Modal/OwnerModal.types';

const title = (data: IOwner) => `${data.pol_oo_name_first} ${data.pol_oo_name_last}`;

const Owners: FC = () => {
  const { id: submissionId } = useParams() as { id: string };
  const { data: submissionData } = useSubmission(submissionId);
  const { relatedResource, performAction } = useExposuresOrGroupFields<IOwner>({
    type: 'group',
    name: 'pol_oo',
  });
  const ownerModalRef = useRef<OwnerModalRef>(undefined);
  const [isDeletingPrimary, setIsDeletingPrimary] = useState(false);

  const entity = submissionData?.data?.policy_data?.pol_legal_entity ?? '';

  const handleAdd = async (data: IOwner) =>
    performAction({
      action: 'add',
      data,
      loadingText: `Adding ${title(data)}`,
    });

  const handleUpdate = async (data: IOwner, id: string) =>
    performAction({
      action: 'update',
      data,
      resourceId: id,
      loadingText: `Updating the ${title(data)}`,
    });

  const handleDelete = async (owner: ExposureOrGroup<IOwner>) =>
    performAction({
      action: 'delete',
      resourceId: owner.id,
      loadingText: `Deleting the ${title(owner.data)}`,
    });

  const handleNext = async () => {
    const entityTypeQuestion = 'entityType';
    const partnership = 'Partnership';
    const businessEntityType = submissionData?.data?.uw_questions[entityTypeQuestion];
    if (businessEntityType === partnership && relatedResource.length < 2) {
      // Business logic: there should be at least 2 owners, if the entityType is Partnership
      displayToastMessage('ERROR', 'There must be at least two owners/officers.');
      return false;
    }

    if (relatedResource.length === 0) {
      displayToastMessage('ERROR', 'There must be at least one owner/officer.');
      return false;
    }
    return true;
  };

  const entityType = useMemo(() => determineOwnerOfficerPositions(entity as string), [entity]);

  const category = useMemo(() => (entity === 'Trust' ? 'trustee' : 'owner'), [entity]);

  return (
    <>
      <OwnerModal
        ref={ownerModalRef}
        handleAdd={handleAdd}
        handleUpdate={handleUpdate}
        category={category}
      />

      <Modal
        open={isDeletingPrimary}
        title={`${capitalize(category)} is Primary`}
        onCloseIconClick={() => setIsDeletingPrimary(false)}
        onClose={() => setIsDeletingPrimary(false)}
        modalContent={
          <Typography>
            You cannot delete primary {category}. To delete this {category}, you need to assign
            primary status to another {category} first.
          </Typography>
        }
      />

      <ExposuresWrapper
        title={entityType.title}
        description={entityType.description}
        addButtonText={`Add ${category}`}
        onAddBtnClick={() => ownerModalRef.current?.openModal()}
        onNext={handleNext}
      >
        <ExposuresList>
          {relatedResource?.map((owner) => (
            <ExposuresListItem
              key={owner.id}
              category={category}
              onDeleteIconClicked={() => {
                if (owner.data.pol_oo_isprimary === 'Yes') {
                  setIsDeletingPrimary(true);
                  return false;
                } else {
                  return true;
                }
              }}
              title={title(owner.data)}
              onEdit={() => ownerModalRef.current?.openModal(owner)}
              onDelete={() => {
                handleDelete(owner);
              }}
            >
              {owner.data.pol_oo_isprimary === 'Yes' && (
                <Typography variant="specialInfo">Primary {category}</Typography>
              )}
            </ExposuresListItem>
          ))}
        </ExposuresList>
      </ExposuresWrapper>
    </>
  );
};
export default Owners;
